import type { GameState } from './types';

/*
 * גיבוב מצב — FNV-1a על כל שדה שמשפיע על המשחק.
 *
 * שני שימושים, ושניהם קריטיים:
 *   1. **טסטים**: "אותו seed נותן אותו משחק" הוא משפט שאי אפשר לבדוק בלי מספר אחד
 *      שמסכם את כל המצב.
 *   2. **זיהוי desync במולטיפלייר**: שני שחקנים משווים hash בכל כמה טיקים. הפרש
 *      אומר שהמצבים התפצלו, וכדאי לדעת את זה מיד ולא אחרי דקה של משחק שונה.
 *
 * **כל שדה חדש במצב חייב להיכנס גם לכאן וגם ל-serialize.** שכחה באחד מהם נתפסת
 * בטסט הדטרמיניזם: שמירה→טעינה→המשך חייב להחזיר בדיוק את אותו hash.
 */

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/**
 * סדר קבוע לסוגי השחקן. **קבוע ולא `Object.keys`** — סדר שנגזר מאובייקט משתנה
 * עם עריכה תמימה, וכל ה-hash של כל השמירות משתנה איתו.
 */
const PLAYER_KIND_ORDER = ['human', 'ai', 'remote'] as const;

/** מגבב מחרוזת קצרה לערך שלם. מזהים בתוכן הם ASCII קצר, ולכן זה מספיק. */
function mixText(text: string): number {
    let value = 0;
    for (let i = 0; i < text.length; i++) {
        value = (value * 33 + text.charCodeAt(i)) >>> 0;
    }
    return value;
}

function mix(hash: number, value: number): number {
    // מערבבים 32 ביט בארבע מנות של בית, כדי ששינוי בביט גבוה לא ייבלע.
    let h = hash;
    for (let shift = 0; shift < 32; shift += 8) {
        h = (h ^ ((value >>> shift) & 0xff)) >>> 0;
        h = Math.imul(h, FNV_PRIME) >>> 0;
    }
    return h;
}

export function hashState(state: GameState): number {
    let h = FNV_OFFSET;

    h = mix(h, state.tick);
    h = mix(h, state.seed);
    /*
     * המפה נכנסת ל-hash למרות שאינה משתנה: ההצדקה השנייה של הפונקציה היא זיהוי
     * desync במולטיפלייר, ושני לקוחות שרצים על מפות שונות היו מקבלים אותו hash
     * עד לרגע שבו יחידה נוגעת בטרשת שונה — כלומר מאוחר מדי כדי להיות שימושי.
     */
    h = mix(h, mixText(state.mapId));
    /*
     * נקודות הפתיחה נכנסות מאותה סיבה שהמפה נכנסת: הן מוגרלות, הן קובעות את כל
     * מצב הפתיחה, ושני לקוחות שהגרילו אחרת חייבים להיראות שונים **מיד**.
     */
    h = mix(h, state.startSlots.length);
    for (const slot of state.startSlots) {
        h = mix(h, slot.x);
        h = mix(h, slot.y);
    }
    h = mix(h, state.nextEntityId);
    h = mix(h, state.rng.a);
    h = mix(h, state.rng.b);
    h = mix(h, state.rng.c);
    h = mix(h, state.rng.d);
    h = mix(h, state.phase);
    h = mix(h, state.winner);

    h = mix(h, state.units.length);
    for (const unit of state.units) {
        h = mix(h, unit.id);
        h = mix(h, unit.owner);
        h = mix(h, unit.type);
        h = mix(h, unit.x);
        h = mix(h, unit.y);
        h = mix(h, unit.hp);
        h = mix(h, unit.mode);
        h = mix(h, unit.goalTile);
        h = mix(h, unit.homeX);
        h = mix(h, unit.homeY);
        h = mix(h, unit.targetId);
        h = mix(h, unit.cooldown);
        h = mix(h, unit.burstLeft);
        h = mix(h, unit.cargo);
        h = mix(h, unit.path.length);
        for (const tile of unit.path) h = mix(h, tile);
    }

    h = mix(h, state.buildings.length);
    for (const building of state.buildings) {
        h = mix(h, building.id);
        h = mix(h, building.owner);
        h = mix(h, building.type);
        h = mix(h, building.tileX);
        h = mix(h, building.tileY);
        h = mix(h, building.hp);
        h = mix(h, building.targetId);
        h = mix(h, building.cooldown);
        h = mix(h, building.burstLeft);
        h = mix(h, building.rallyTile);
        h = mix(h, building.repairing);
    }

    h = mix(h, state.projectiles.length);
    for (const projectile of state.projectiles) {
        h = mix(h, projectile.id);
        h = mix(h, projectile.owner);
        h = mix(h, projectile.type);
        h = mix(h, projectile.x);
        h = mix(h, projectile.y);
        h = mix(h, projectile.targetId);
        h = mix(h, projectile.lastX);
        h = mix(h, projectile.lastY);
        h = mix(h, projectile.damage);
        h = mix(h, projectile.warhead);
    }

    h = mix(h, state.players.length);
    for (const player of state.players) {
        h = mix(h, player.index);
        // אנושי/מחשב/מרוחק — הבדל שמשנה מי שולח פקודות, ולכן שייך לזיהוי desync.
        h = mix(h, PLAYER_KIND_ORDER.indexOf(player.kind));
        h = mix(h, player.credits);
        h = mix(h, player.unitsBuilt);
        h = mix(h, player.unitsLost);
        /*
         * הזיכרון של ערפל הקרב נכנס ל-hash כי הוא **חלק מהמצב** ולא תצוגה: שני
         * לקוחות שחשפו אזורים שונים חייבים להיראות שונים, אחרת טסט הדטרמיניזם
         * יעבור גם אם `explored` נשכח בשמירה.
         */
        h = mix(h, player.explored.length);
        for (let i = 0; i < player.explored.length; i++) h = mix(h, player.explored[i] ?? 0);
        h = mix(h, player.defeated ? 1 : 0);
        h = mix(h, player.difficulty);
        h = mix(h, player.queues.length);
        for (const queue of player.queues) {
            h = mix(h, queue.length);
            for (const item of queue) {
                h = mix(h, item.kind);
                h = mix(h, item.type);
                h = mix(h, item.paid);
                h = mix(h, item.ready ? 1 : 0);
            }
        }
    }

    h = mix(h, state.pathQueue.length);
    for (const id of state.pathQueue) h = mix(h, id);

    // העפרות משתנות רק כשכורים; מגבבים את כולן כדי שגם זה ייתפס כשיגיע.
    for (let i = 0; i < state.ore.length; i++) {
        const amount = state.ore[i] ?? 0;
        if (amount !== 0) {
            h = mix(h, i);
            h = mix(h, amount);
        }
    }

    return h >>> 0;
}

/** ייצוג קצר וקריא ל-overlay הדיבאג ולהודעות desync. */
export function formatHash(hash: number): string {
    return hash.toString(16).padStart(8, '0');
}
